import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAppSurface } from '../chrome.js';
import { appBaseUrl } from '../config.js';

/**
 * A link to a destination that lives ONLY in the analyzer — a run's report (`/analyses/:id`) or a
 * project's deals (`/analyses?project=`) — from a page rendered by either application.
 *
 * 🔴 ON THE HUB AN IN-APP LINK HERE IS A SILENT REDIRECT, NOT A 404. `HubApp.tsx` has no `/analyses`
 * routes and its catch-all is `<Navigate to="/projects">`, so `navigate('/analyses/…')` from a Hub page
 * dropped the reader on the project list with no error anywhere — #1393's dead deals link, and the
 * "screen flipped" after a re-underwrite in #1396. So the Hub gets a real cross-origin `<a href>` to
 * `appBaseUrl`, and the analyzer keeps its in-app `<Link>` (no full reload, same history stack).
 *
 * ⚠️ `null` (a root that forgot its `SurfaceProvider`) is treated as the analyzer, matching chrome.tsx's
 * "null means NOT the Hub". That is the safe direction here too: the in-app link is what every page did
 * before this component existed.
 *
 * Both branches are real anchors, so the link can be middle-clicked and copied like any address.
 *
 * @param to an absolute in-app path, starting with `/`.
 */
export function AnalyzerLink({
  to,
  className,
  children,
}: {
  to: string;
  className?: string;
  children: ReactNode;
}) {
  const surface = useAppSurface();
  /*
    ⚠️ NO ANALYZER ORIGIN (a local build with no `api.` host and no `VITE_APP_BASE_URL`): the words,
    with no link. Both alternatives are wrong links — in-app is the very `/projects` bounce this
    component exists to remove, and the api origin answers a report path with a JSON 404. A missing
    link is visibly missing; a wrong one teaches the reader the product is broken.
  */
  if (surface === 'hub' && appBaseUrl === null) {
    return <span title="The analyzer's address is not configured for this build">{children}</span>;
  }
  if (surface === 'hub') {
    return (
      <a className={className} href={`${appBaseUrl}${to}`}>
        {children}
      </a>
    );
  }
  return (
    <Link className={className} to={to}>
      {children}
    </Link>
  );
}
